import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/kfmApi";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowRight, BookOpen } from "lucide-react";

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
}

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    document.title = "Blog — OpenWord AI";
    api.get("/blog").then(({ data }) => setPosts(data.posts || [])).catch(() => setPosts([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#07090E]">
      <header className="border-b border-[#1E273D] bg-[#0E131F]/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="blog-logo-home">
            <img src="/openword-logo-horizontal.png" alt="OpenWord AI" className="h-6" />
          </Link>
          <nav className="flex items-center gap-4 sm:gap-6 text-sm text-slate-400">
            <Link to="/" data-testid="blog-nav-home" className="hover:text-[#00E5FF] transition-colors">Home</Link>
            <Link to="/pricing" data-testid="blog-nav-pricing" className="hover:text-[#00E5FF] transition-colors">Pricing</Link>
          </nav>
          <Link
            to={user ? "/study" : "/login"}
            data-testid="blog-cta-header"
            className="text-sm font-medium text-[#00E5FF] hover:text-[#00B8CC]"
          >
            {user ? "Back to Study" : "Try free"}
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <div className="text-xs font-medium uppercase tracking-[0.25em] text-[#00E5FF] mb-2">The OpenWord AI Blog</div>
        <h1 className="text-4xl sm:text-5xl text-white leading-tight">Reflections, rooted in Scripture.</h1>
        <p className="text-base text-slate-400 mt-3 leading-relaxed">
          Short, honest reflections on what we&rsquo;re all living through — and what the Word actually says about it.
        </p>

        {posts === null && (
          <div className="flex items-center gap-2 text-slate-500 mt-12"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        )}
        {posts && posts.length === 0 && (
          <p className="text-slate-500 mt-12">No posts yet — check back soon.</p>
        )}

        <div className="mt-10 space-y-4" data-testid="blog-list">
          {(posts || []).map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group block rounded-2xl bg-[#0E131F] border border-[#1E273D] hover:border-[#00E5FF]/40 overflow-hidden transition-colors"
            >
              {p.has_photo && (
                <div className="aspect-[16/9] w-full overflow-hidden bg-[#141B29]">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/blog/${p.slug}/post-image.png?shape=landscape`}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              )}
              <div className={p.has_photo ? "p-6" : "p-6"}>
              <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2">
                <BookOpen className="w-3.5 h-3.5 text-[#00E5FF]" /> {fmtDate(p.published_at)}
              </div>
              <h2 className="text-2xl text-white mt-2 group-hover:text-[#00E5FF] transition-colors">{p.title}</h2>
              {p.excerpt && <p className="text-slate-400 mt-2 leading-relaxed">{p.excerpt}</p>}
              <span className="inline-flex items-center gap-1 text-sm font-medium text-[#00E5FF] mt-3">
                Read <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
